/**
 * SupportButton.js - Floating support button for workers and customers
 * Shows support status (online/offline) and provides quick access to support options
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Headphones, MessageSquare, Phone, Video, X, Send,
  Circle, ChevronUp, FileText, Voicemail, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../services/api';
import VoiceRecorder from './VoiceRecorder';
import notificationService from '../../services/notificationService';

const SupportButton = ({ 
  currentUser, 
  onStartChat, 
  onStartCall,
  websocket,
  position = 'bottom-right' // bottom-right, bottom-left
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [supportStatus, setSupportStatus] = useState({ available: false, online_owners: [] });
  const [showForm, setShowForm] = useState(false);
  const [showVoicemail, setShowVoicemail] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'normal',
    category: 'general',
    callback_requested: false,
    preferred_contact: 'chat'
  });
  const [submitting, setSubmitting] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // Check and request notification permission on mount
  useEffect(() => {
    const checkNotifications = async () => {
      const status = notificationService.getPermissionStatus();
      if (status === 'granted') {
        setNotificationsEnabled(true);
      } else if (status === 'default') {
        // Auto-request on first interaction
        const granted = await notificationService.requestPermission();
        setNotificationsEnabled(granted);
      }
    };
    checkNotifications();
  }, []);

  // Check support status
  const checkSupportStatus = useCallback(async () => {
    try {
      const res = await api.get('/support/owner-status');
      setSupportStatus(res.data);
    } catch (error) {
      console.error('Failed to check support status:', error);
    }
  }, []);

  useEffect(() => {
    checkSupportStatus();
    // Poll every 30 seconds
    const interval = setInterval(checkSupportStatus, 30000);
    return () => clearInterval(interval);
  }, [checkSupportStatus]);

  // Update user presence when component mounts
  useEffect(() => {
    const updatePresence = async () => {
      try {
        await api.post('/support/presence', { status: 'online' });
      } catch (error) {
        console.error('Failed to update presence:', error);
      }
    };
    
    updatePresence();
    
    // Update presence every minute
    const interval = setInterval(updatePresence, 60000);
    
    // Set offline when leaving
    return () => {
      clearInterval(interval);
      api.post('/support/presence', { status: 'offline' }).catch(() => {});
    };
  }, []);

  // Handle form submission
  const handleSubmitForm = async () => {
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/support/tickets', formData);
      toast.success('Support request submitted! We\'ll get back to you soon.');
      setFormData({
        subject: '',
        message: '',
        priority: 'normal',
        category: 'general',
        callback_requested: false,
        preferred_contact: 'chat'
      });
      setShowForm(false);
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle start chat
  const handleStartChat = () => {
    if (supportStatus.online_owners?.[0]) {
      onStartChat?.({
        user_id: supportStatus.online_owners[0].id,
        user_name: supportStatus.online_owners[0].name,
        user_role: 'owner'
      });
    } else {
      // Create support conversation anyway
      onStartChat?.({
        support: true
      });
    }
    setIsOpen(false);
  };

  // Handle start call
  const handleStartCall = (type) => {
    if (!supportStatus.available) {
      toast.error('Support is currently offline. Please leave a voicemail or submit a form.');
      return;
    }
    
    if (supportStatus.online_owners?.[0]) {
      onStartCall?.({
        user_id: supportStatus.online_owners[0].id,
        user_name: supportStatus.online_owners[0].name,
        user_role: 'owner'
      }, type);
    }
    setIsOpen(false);
  };

  const positionClasses = position === 'bottom-left' 
    ? 'left-4 bottom-4' 
    : 'right-4 bottom-4';

  return (
    <>
      {/* Floating Button */}
      <div className={`fixed ${positionClasses} z-40`}>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="absolute bottom-16 right-0 w-72 bg-[var(--background-paper)] rounded-2xl shadow-2xl border border-[var(--border-subtle)] overflow-hidden"
            >
              {/* Header */}
              <div className="p-4 bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 border-b border-[var(--border-subtle)]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Headphones className="w-5 h-5 text-[#D4AF37]" />
                    <span className="font-semibold">Support</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Circle className={`w-2 h-2 ${supportStatus.available ? 'text-green-500 fill-green-500' : 'text-gray-500 fill-gray-500'}`} />
                    <span className="text-xs text-[var(--text-muted)]">
                      {supportStatus.available ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                {supportStatus.available ? (
                  <>
                    <p className="text-sm text-[var(--text-muted)]">Support is available now!</p>
                    
                    <button
                      onClick={handleStartChat}
                      className="w-full p-3 rounded-xl bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 transition-all flex items-center gap-3"
                      data-testid="support-chat-btn"
                    >
                      <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
                      <span>Start Live Chat</span>
                    </button>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleStartCall('voice')}
                        className="flex-1 p-3 rounded-xl bg-green-500/20 hover:bg-green-500/30 transition-all flex items-center justify-center gap-2"
                        data-testid="support-voice-btn"
                      >
                        <Phone className="w-5 h-5 text-green-500" />
                        <span className="text-sm">Voice</span>
                      </button>
                      <button
                        onClick={() => handleStartCall('video')}
                        className="flex-1 p-3 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 transition-all flex items-center justify-center gap-2"
                        data-testid="support-video-btn"
                      >
                        <Video className="w-5 h-5 text-blue-500" />
                        <span className="text-sm">Video</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-[var(--text-muted)]">Support is offline. Leave a message:</p>
                    
                    <button
                      onClick={() => { setShowVoicemail(true); setIsOpen(false); }}
                      className="w-full p-3 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 transition-all flex items-center gap-3"
                      data-testid="leave-voicemail-btn"
                    >
                      <Voicemail className="w-5 h-5 text-purple-500" />
                      <span>Leave Voicemail</span>
                    </button>
                    
                    <button
                      onClick={() => { setShowForm(true); setIsOpen(false); }}
                      className="w-full p-3 rounded-xl bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 transition-all flex items-center gap-3"
                      data-testid="submit-form-btn"
                    >
                      <FileText className="w-5 h-5 text-[#D4AF37]" />
                      <span>Submit Support Form</span>
                    </button>
                  </>
                )}
                
                <div className="pt-2 border-t border-[var(--border-subtle)]">
                  <button
                    onClick={() => { setShowForm(true); setIsOpen(false); }}
                    className="w-full text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    Or submit a detailed request
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
            supportStatus.available 
              ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8860B]' 
              : 'bg-gray-700'
          }`}
          data-testid="support-floating-btn"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Headphones className="w-6 h-6 text-white" />
          )}
          
          {/* Online indicator */}
          {supportStatus.available && !isOpen && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-[var(--background)] animate-pulse" />
          )}
        </motion.button>
      </div>

      {/* Support Form Modal */}
      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-[var(--border-subtle)]">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#D4AF37]" />
                    Support Request
                  </h3>
                  <button onClick={() => setShowForm(false)}>
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <Label>Subject *</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief description of your issue"
                  />
                </div>
                
                <div>
                  <Label>Category</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-[var(--background-elevated)] border border-[var(--border-subtle)]"
                  >
                    <option value="general">General Question</option>
                    <option value="billing">Billing Issue</option>
                    <option value="technical">Technical Problem</option>
                    <option value="complaint">Complaint</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <Label>Priority</Label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full p-2.5 rounded-lg bg-[var(--background-elevated)] border border-[var(--border-subtle)]"
                  >
                    <option value="low">Low - Can wait</option>
                    <option value="normal">Normal</option>
                    <option value="high">High - Need help soon</option>
                    <option value="urgent">Urgent - Blocking issue</option>
                  </select>
                </div>
                
                <div>
                  <Label>Message *</Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Describe your issue in detail..."
                    rows={5}
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="callback"
                    checked={formData.callback_requested}
                    onChange={(e) => setFormData({ ...formData, callback_requested: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <label htmlFor="callback" className="text-sm">Request a callback</label>
                </div>
                
                {formData.callback_requested && (
                  <div>
                    <Label>Preferred Contact Method</Label>
                    <select
                      value={formData.preferred_contact}
                      onChange={(e) => setFormData({ ...formData, preferred_contact: e.target.value })}
                      className="w-full p-2.5 rounded-lg bg-[var(--background-elevated)] border border-[var(--border-subtle)]"
                    >
                      <option value="chat">Chat</option>
                      <option value="call">Phone Call</option>
                      <option value="email">Email</option>
                    </select>
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSubmitForm} 
                    disabled={submitting}
                    className="btn-primary"
                  >
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Voicemail Modal - Using VoiceRecorder component */}
      {showVoicemail && (
        <VoiceRecorder
          recipientId={supportStatus.online_owners?.[0]?.id || 'support'}
          recipientName="Support"
          onClose={() => setShowVoicemail(false)}
          onSuccess={() => {
            setShowVoicemail(false);
            toast.success('Voicemail sent successfully!');
          }}
        />
      )}
    </>
  );
};

export default SupportButton;
